/**
 * What a bay has in it besides its tool.
 *
 * A fab aisle is not a tool face every ten metres and white wall between:
 * the wall carries the things a technician uses and is trained on. Here,
 * per bay, in the order the eye meets them walking in: the chase door
 * beside the face, the andon board over it, the wipe and glove dispensers
 * on the pilaster, the extinguisher, the eyewash and safety shower at the
 * chemical bays, the exit signs at the ends and the evacuation map by the
 * airlock, and the sticky mat inside the airlock door. Under the hoist
 * rail, between the faces, the side-track buffers with their carriers -
 * where a current fab keeps a lot that is a few minutes from its tool -
 * and beside the scanner, the reticle stocker.
 *
 * And one tool down: THRM-04 with its skin off, a lockout tag on the
 * open panel and a technician on a stool inside it (the person is in
 * people.ts; the stool, the panel and the tag are here). A floor where
 * every tool is up and nobody is fixing anything is a showroom.
 *
 * All of it is static and built once; the andon boards repaint from the
 * same readouts the HMI screens do.
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import {
  aisleDirection,
  bayNumber,
  BULKHEAD_Z,
  FAB,
  GOWNING,
  SIDE_REACH,
  TOOLS,
  toolFaceZ,
  toolSize,
  type Tool,
} from './layout'
import {
  box,
  canvasTexture,
  cyl,
  emoFaceX,
  EMO_RED,
  mergeStatics,
  partitions,
  partitionZ,
  skinSpan,
  STOCKER,
  tint,
  UNLIT,
  type Attachments,
  type FabMaterials,
} from './builders'
import { BALLROOM } from './hall'
import type { HmiReadout } from './hmi'

// The side furniture's reach lives in layout.ts now, where the ballroom's
// row packing needs it; re-exported so its readers here need not move.
export { SIDE_REACH }

/** The tool that is down for maintenance. */
export const MAINTENANCE_TOOL = 'thermal'

/**
 * Where on the down tool's face the skin is off - and WHY IT IS DERIVED.
 *
 * It used to be a hand-written +0.3 m. That was clear skin when it was
 * written, and it is now the middle of the front end: THRM-04's glass starts
 * at +0.46 and the HMI hangs off its outboard corner on a 0.62 m arm, with a
 * 0.78 x 0.5 screen at 1.5 m - head height. The technician works the panel
 * from 0.62 m off the same face, so the screen hung in exactly the plane his
 * head occupies and swallowed it from every angle except head-on. He was a
 * gowned figure with no head above the shoulders for a whole session, and
 * nothing about the man was wrong: he was standing inside the monitor.
 *
 * So it is worked out from the face rather than written down. The panel goes
 * on what is left of the mainframe skin once the front end, the HMI bezel and
 * the EMO have taken their share - which is where a PM comes off anyway. You
 * do not take a mini-environment apart to get at a heater.
 *
 * And it is a LOWER panel, 1.5 m to the floor rather than 1.9. The skin it
 * comes off is also where the furnace carries its hazard placards - process
 * gas, and the nitrogen the tube idles under - and a hole where those were is
 * a face that has stopped teaching the one thing this bay is for. They sit at
 * 1.55 and up; the panel stops under them.
 */
const PANEL_W = 0.8
export const MAINTENANCE_PANEL = { x: maintenancePanelX(), w: PANEL_W, h: 1.3, y: 0.85 }

function maintenancePanelX(): number {
  const tool = TOOLS.find((t) => t.id === MAINTENANCE_TOOL)
  if (!tool) return 0
  // From the skin's outboard edge to the EMO, which is the first thing the
  // HMI end of the face puts in the way.
  const lo = skinSpan(tool)[0] + 0.06 + PANEL_W / 2
  const hi = emoFaceX(tool) - 0.24 - PANEL_W / 2
  return hi > lo ? (lo + hi) / 2 : lo
}

export interface Bays {
  group: THREE.Group
  /** Repaint a bay's andon from its tool's readout. */
  update(tool: Tool, readout: HmiReadout, processName: string): void
  /** Swing the chase doors for whoever is at them. */
  animate(dt: number, eye: THREE.Vector3): void
  colliders(): Array<{ minX: number; maxX: number; minZ: number; maxZ: number }>
  /**
   * Where the wall is cut: the doorways and the windows, for the chase to
   * build against (an extinguisher beside every door, clear of its swing).
   */
  openings(): {
    doors: Array<{ x: number; side: number; toolId: string }>
    windows: Array<{ x: number; side: number; w: number }>
  }
}

/** A chase door: where it is, and its leaf on its hinge. `open` is 0..1. */
interface ChaseDoor {
  x: number
  side: number
  /** Whose bay it serves - which says which side of it the leaf swings clear of. */
  toolId: string
  leaf: THREE.Group
  open: number
}
const DOOR_W = 0.95
const DOOR_H = 2.1

const SAFETY_GREEN = new THREE.MeshStandardMaterial({ color: 0x1f9d55, roughness: 0.6 })
const EXIT_GREEN = [0.2, 2.0, 0.6] as const
const TAG_RED_TINT = [0.7, 0.05, 0.06] as const
const MAT_BLUE = new THREE.MeshStandardMaterial({ color: 0x3a6fb5, roughness: 0.9 })
const DISPENSER = new THREE.MeshStandardMaterial({ color: 0xf7f9fb, roughness: 0.5 })
const CAVITY = new THREE.MeshStandardMaterial({ color: 0x2a2f36, roughness: 0.9 })
const RETICLE_POD = new THREE.MeshStandardMaterial({ color: 0x5e6a78, roughness: 0.4, metalness: 0.3 })

/** One material per distinct sign, so the same sign on twenty walls is one draw. */
const signCache = new Map<string, THREE.MeshBasicMaterial>()
function signMaterial(text: string, fill: string, ink: string, w = 256, h = 96): THREE.MeshBasicMaterial {
  const key = `${text}|${fill}|${ink}|${w}x${h}`
  const cached = signCache.get(key)
  if (cached) return cached
  const tex = canvasTexture(w, h, (ctx) => {
    ctx.fillStyle = fill
    ctx.fillRect(0, 0, w, h)
    ctx.fillStyle = ink
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = `bold ${Math.round(h * 0.42)}px system-ui, sans-serif`
    ctx.fillText(text, w / 2, h / 2)
  })
  tex.colorSpace = THREE.SRGBColorSpace
  const m = new THREE.MeshBasicMaterial({ map: tex })
  signCache.set(key, m)
  return m
}

/** A plane on the bulkhead at world (x, y), facing the aisle on `side`. */
function onWall(
  g: THREE.Group,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  x: number,
  y: number,
  side: number,
  proud = 0.006,
): THREE.Mesh {
  const m = new THREE.Mesh(geometry, material)
  m.rotation.y = side > 0 ? Math.PI : 0
  m.position.set(x, y, side * (BULKHEAD_Z - 0.1 - proud))
  g.add(m)
  return m
}

/** A box against the bulkhead, its back on the wall. */
function againstWall(g: THREE.Group, w: number, h: number, d: number, material: THREE.Material, x: number, y: number, side: number): THREE.Mesh {
  const m = new THREE.Mesh(box(w, h, d), material)
  m.position.set(x, y, side * (BULKHEAD_Z - 0.1 - d / 2))
  g.add(m)
  return m
}

/**
 * The andon: the bay's own board, above the chase door, with the tool's
 * tag, its state in the colour the tower shows, and the numbers a bay
 * manager reads walking past - lots waiting, moves this shift, up-time.
 * The moves and the up-time are steady per tool, the waiting count is the
 * live one: a tool that is running has a lot in it.
 */
const ANDON_W = 384
const ANDON_H = 192
/**
 * Every andon on one canvas, a tile each, behind one material - so the
 * boards merge into a single draw with the rest of the wall furniture
 * instead of twenty draws with twenty textures. A repaint touches one
 * tile and re-uploads the atlas, which is why `update` only repaints
 * when what the board would say has changed.
 */
const ANDON_TILES = 24
const andonCanvas = document.createElement('canvas')
andonCanvas.width = ANDON_W
andonCanvas.height = ANDON_H * ANDON_TILES
const andonTexture = new THREE.CanvasTexture(andonCanvas)
andonTexture.colorSpace = THREE.SRGBColorSpace
const ANDON_MAT = new THREE.MeshBasicMaterial({ map: andonTexture })
/** A board's quad, its UVs on its own tile of the atlas. */
function andonQuad(tile: number): THREE.BufferGeometry {
  const geo = new THREE.PlaneGeometry(1.2, 0.6)
  const uv = geo.attributes.uv
  const v1 = 1 - tile / ANDON_TILES
  const v0 = 1 - (tile + 1) / ANDON_TILES
  for (let i = 0; i < uv.count; i++) uv.setY(i, v0 + uv.getY(i) * (v1 - v0))
  return geo
}
const BAND = { idle: '#7d858f', running: '#0ca30c', alarm: '#fab219', down: '#d8262c' } as const

/** A glazed opening in the bulkhead: its centre, side, and width. */
interface ChaseWindow {
  x: number
  side: number
  w: number
}
const WINDOW_W = 1.6
const WINDOW_SILL = 0.85
const WINDOW_HEAD = 2.45

/**
 * What stands in the chase behind a window: the tool's cooling and air
 * drops, off the headers chase.ts runs along the wall.
 *
 * THIS WAS A DIORAMA, AND THE DIORAMA IS GONE. When the chase was bare and
 * seen only through this window, a dark backdrop, six painted pipes from
 * floor to deck, a dry pump raised to sill height and a cable tray were
 * put here so the window looked onto something. Then the chase became a
 * room a student walks, and from inside every one of them was wrong: the
 * pipes tied into nothing at either end, the tray ran through the CDA
 * header at its own height, and the raised pump was a second, darker pump
 * beside the real one `buildTool` stands on the floor. Twenty windows of
 * that made the corridor a pipe display.
 *
 * What stays is what a window would actually see: three drops - PCW
 * supply, PCW return, CDA - leaving their headers (SPINE in chase.ts: 1.32,
 * 1.55 and 1.05 m out, 2.72 / 2.72 / 2.62 up), painted in the headers' own
 * service colours, through an isolation valve at hand height and on down
 * to the floor. The rest of the view is the chase itself: the pump and gas
 * box beside the mainframe, the support bank behind, the line-up at the
 * far wall.
 */
const DROPS = [
  { d: 1.32, y: 2.72, colour: 0x4b8fd0, dx: -0.42 }, // PCW supply
  { d: 1.55, y: 2.72, colour: 0x3f6f9e, dx: -0.18 }, // PCW return
  { d: 1.05, y: 2.62, colour: 0xd8dde2, dx: 0.48 }, // CDA
] as const
const DROP_MATS = DROPS.map((dr) => new THREE.MeshStandardMaterial({ color: dr.colour, roughness: 0.55, metalness: 0.25 }))
function dressChase(g: THREE.Group, mats: FabMaterials, win: ChaseWindow): void {
  const side = win.side
  const z = (d: number): number => side * (BULKHEAD_Z + d)
  const VALVE_Y = 1.15
  DROPS.forEach((dr, i) => {
    const x = win.x + dr.dx
    const top = dr.y - 0.06
    // The tee under the header, the run down to the valve, the valve with
    // its handwheel, and the run on to the floor.
    const tee = new THREE.Mesh(cyl(0.056, 0.056, 0.14, 10), mats.steel)
    tee.position.set(x, top, z(dr.d))
    g.add(tee)
    const upper = new THREE.Mesh(cyl(0.038, 0.038, top - VALVE_Y - 0.06, 10), DROP_MATS[i])
    upper.position.set(x, (top + VALVE_Y + 0.06) / 2, z(dr.d))
    g.add(upper)
    const valve = new THREE.Mesh(box(0.13, 0.12, 0.12), mats.steel)
    valve.position.set(x, VALVE_Y, z(dr.d))
    g.add(valve)
    const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.06, 0.011, 8, 20), mats.conduitDark)
    wheel.rotation.y = Math.PI / 2
    wheel.position.set(x + 0.085, VALVE_Y, z(dr.d))
    g.add(wheel)
    // Down to the floor, then an elbow and a run into the trench along the
    // wall (chase.ts grates it at 0.85 m out), under which it reaches the
    // tool. A drop that stopped on the floor was a pipe connected to nothing.
    const KNEE = 0.12
    const lower = new THREE.Mesh(cyl(0.038, 0.038, VALVE_Y - 0.06 - KNEE, 10), DROP_MATS[i])
    lower.position.set(x, (VALVE_Y - 0.06 + KNEE) / 2, z(dr.d))
    g.add(lower)
    const elbow = new THREE.Mesh(new THREE.SphereGeometry(0.046, 10, 8), DROP_MATS[i])
    elbow.position.set(x, KNEE, z(dr.d))
    g.add(elbow)
    const TRENCH_D = 0.95
    if (dr.d > TRENCH_D) {
      const run = new THREE.Mesh(cyl(0.038, 0.038, dr.d - TRENCH_D, 10), DROP_MATS[i])
      run.rotation.x = Math.PI / 2
      run.position.set(x, KNEE, z((dr.d + TRENCH_D) / 2))
      g.add(run)
    }
    // The clamp holding it to the wall bracket at shoulder height.
    const clamp = new THREE.Mesh(box(0.1, 0.05, dr.d - 0.1), mats.steel)
    clamp.position.set(x, 1.7, z(dr.d / 2 + 0.05))
    g.add(clamp)
  })
}

/**
 * The bulkhead itself: one wall each side of the hall, floor to ceiling,
 * end to end, with the tool faces standing proud of it and a viewing
 * window beside each face. One merged mesh per side - it is one wall, and
 * the openings are what make it more than a box. The chase behind it
 * (mainframes, chambers, the utility run and its drops) is what the
 * windows look onto.
 */
function buildBulkhead(mats: FabMaterials, windows: ChaseWindow[], doors: ChaseDoor[]): THREE.Mesh[] {
  const H = FAB.ceilingHeight
  const L = FAB.length
  const T = 0.2
  const out: THREE.Mesh[] = []
  const m = new THREE.Matrix4()
  const slab = (list: THREE.BufferGeometry[], x0: number, x1: number, y0: number, y1: number, z: number, t = T): void => {
    if (x1 - x0 < 0.01 || y1 - y0 < 0.01) return
    const geo = new THREE.BoxGeometry(x1 - x0, y1 - y0, t).applyMatrix4(
      m.makeTranslation((x0 + x1) / 2, (y0 + y1) / 2, z),
    )
    // THE PANEL PITCH IS A LENGTH, NOT A FRACTION.
    //
    // A BoxGeometry's UVs run 0..1 across whatever it happens to be, and this
    // wall is cut into slabs by every door and window in it - so a 5 m run
    // between two bays and the 0.9 m stub beside a door were each getting the
    // texture's 96 repeats across their own width. Panels 50 mm wide next to
    // panels 300 mm wide, and neither of them the 1.4 m a prefabricated
    // cleanroom panel actually is. It read as fine ribbing rather than
    // panelling, and it stopped being a scale cue: this is the surface that
    // fills most of every frame down the aisle, and the length of one panel is
    // what the eye measures the room against.
    //
    // Remapped to the hall's own x, so 96 repeats span 136 m however the wall
    // is chopped up, and every panel on the floor is the same 1.42 m.
    const pos = geo.attributes.position
    const uv = geo.attributes.uv
    for (let i = 0; i < pos.count; i++) uv.setXY(i, (pos.getX(i) + L / 2) / L, pos.getY(i) / H)
    list.push(geo)
  }
  for (const side of [-1, 1]) {
    const z = side * BULKHEAD_Z
    // Every opening on this side, windows and doorways together, in x order.
    const openings = [
      ...windows.filter((w) => w.side === side).map((w) => ({ x: w.x, w: w.w, y0: WINDOW_SILL, y1: WINDOW_HEAD, glazed: true })),
      ...doors.filter((d) => d.side === side).map((d) => ({ x: d.x, w: DOOR_W + 0.04, y0: 0, y1: DOOR_H + 0.02, glazed: false })),
    ].sort((a, b) => a.x - b.x)
    const wall: THREE.BufferGeometry[] = []
    const skin: THREE.BufferGeometry[] = []
    const frames: THREE.BufferGeometry[] = []
    const panes: THREE.BufferGeometry[] = []
    // Every run of wall is also a run of SKIN: a thin dark slab on the
    // chase face, a couple of millimetres off it. The back of a cleanroom
    // panel wall is not the cleanroom's white - it is the unfinished side,
    // primer grey with the same joints - and until this the chase face was
    // the brightest thing in the chase.
    const wallSlab = (x0: number, x1: number, y0: number, y1: number): void => {
      slab(wall, x0, x1, y0, y1, z)
      slab(skin, x0, x1, y0, y1, z + side * (T / 2 + 0.009), 0.014)
    }
    let x = -L / 2 + 0.2
    for (const o of openings) {
      const x0 = o.x - o.w / 2
      const x1 = o.x + o.w / 2
      wallSlab(x, x0, 0, H)
      wallSlab(x0, x1, 0, o.y0)
      wallSlab(x0, x1, o.y1, H)
      // The frame: a mullion round the opening, a hand proud of the wall.
      const f = 0.06
      const zf = z - side * 0.02
      if (o.y0 > 0) frames.push(new THREE.BoxGeometry(o.w + 2 * f, f, T + 0.04).applyMatrix4(m.makeTranslation(o.x, o.y0 - f / 2, zf)))
      frames.push(new THREE.BoxGeometry(o.w + 2 * f, f, T + 0.04).applyMatrix4(m.makeTranslation(o.x, o.y1 + f / 2, zf)))
      for (const dx of [x0 - f / 2, x1 + f / 2]) {
        frames.push(new THREE.BoxGeometry(f, o.y1 - o.y0, T + 0.04).applyMatrix4(m.makeTranslation(dx, (o.y0 + o.y1) / 2, zf)))
      }
      if (o.glazed) panes.push(new THREE.BoxGeometry(o.w, o.y1 - o.y0, 0.012).applyMatrix4(m.makeTranslation(o.x, (o.y0 + o.y1) / 2, z)))
      x = x1
    }
    wallSlab(x, L / 2 - 0.2, 0, H)
    const wallMesh = new THREE.Mesh(mergeGeometries(wall, false), mats.wall)
    wallMesh.receiveShadow = true
    // DRAWN FIRST. The wall is the biggest occluder in the room, and three
    // sorts opaque objects by the distance of their centres - the centre of
    // a 136 m wall is mid-hall, so from anywhere else it sorted as far and
    // was painted AFTER the whole chase behind it: every mainframe, chamber
    // and pump was shaded in full and then covered, every frame. That was
    // 24 fps at 164 draw calls on the integrated GPU. Painted first, the
    // depth test rejects all of it before it is shaded.
    wallMesh.renderOrder = -10
    out.push(wallMesh)
    // The chase face: the same panel map, so the joints line up with the
    // wall's own, under a primer grey. Drawn right after the wall, for the
    // same occluder reason.
    const skinMesh = new THREE.Mesh(
      mergeGeometries(skin, false),
      new THREE.MeshStandardMaterial({ map: mats.wall.map, color: 0x8b9198, roughness: 0.84, metalness: 0.16 }),
    )
    skinMesh.renderOrder = -9
    out.push(skinMesh)
    if (frames.length) out.push(new THREE.Mesh(mergeGeometries(frames, false), mats.toolPanel))
    if (panes.length) out.push(new THREE.Mesh(mergeGeometries(panes, false), mats.glass))
  }
  return out
}

function paintAndon(tile: number, bay: string, tool: Tool, readout: HmiReadout | null, processName: string, down: boolean): string {
  const ctx = andonCanvas.getContext('2d')!
  ctx.save()
  ctx.translate(0, tile * ANDON_H)
  const key = paintAndonTile(ctx, bay, tool, readout, processName, down)
  ctx.restore()
  return key
}

/** What an andon board says, besides its colour. */
export type AndonState = 'idle' | 'running' | 'alarm' | 'down'

/**
 * The three numbers on a bay's board: lots waiting, moves this shift, and
 * up-time. Steady per tool - they are a bay manager's glance, not a
 * simulation - except the waiting count, which follows the state, because
 * a tool that is running has a lot in it and a tool that is down has a
 * queue building behind it.
 *
 * Exported because the shift supervisor reads these boards as he walks the
 * aisle (talk/brief.ts) and quotes them to students. One definition, so
 * the number he says cannot differ from the number painted on the board
 * the student is standing under - the same rule `bayNumber` and
 * `displayedReadout` follow.
 */
export function andonNumbers(tool: Tool, state: AndonState): { wip: number; moves: number; uptime: number } {
  const seed = tool.assetTag.split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return {
    wip: state === 'running' ? 1 + (seed % 3) : state === 'down' ? 4 + (seed % 4) : seed % 3,
    moves: 60 + (seed % 90),
    uptime: state === 'down' ? 0 : 88 + (seed % 11),
  }
}

function paintAndonTile(ctx: CanvasRenderingContext2D, bay: string, tool: Tool, readout: HmiReadout | null, processName: string, down: boolean): string {
  ctx.fillStyle = '#0b1622'
  ctx.fillRect(0, 0, ANDON_W, ANDON_H)
  const state = down && (!readout || readout.state === 'idle') ? 'down' : (readout?.state ?? 'idle')
  ctx.fillStyle = BAND[state]
  ctx.fillRect(0, 0, ANDON_W, 52)
  ctx.fillStyle = state === 'alarm' ? '#14181d' : '#ffffff'
  ctx.font = 'bold 30px system-ui, sans-serif'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(`BAY ${bay}`, 14, 27)
  ctx.textAlign = 'right'
  ctx.fillText(tool.assetTag, ANDON_W - 14, 27)
  const label = state === 'down' ? 'DOWN · PM' : state === 'alarm' ? 'ALARM' : state === 'running' ? 'RUNNING' : 'IDLE'
  ctx.fillStyle = '#ffffff'
  ctx.font = 'bold 40px system-ui, sans-serif'
  ctx.textAlign = 'left'
  ctx.fillText(label, 14, 92)
  ctx.fillStyle = '#9aa4ae'
  ctx.font = '18px system-ui, sans-serif'
  ctx.fillText(processName.toUpperCase().slice(0, 28), 14, 122)
  // Three tiles: WIP, moves, uptime.
  const { wip, moves, uptime } = andonNumbers(tool, state)
  const tiles: Array<[string, string]> = [
    ['WIP', String(wip)],
    ['MOVES', String(moves)],
    ['UPTIME', `${uptime}%`],
  ]
  tiles.forEach(([k, v], i) => {
    const x = 14 + i * 122
    ctx.fillStyle = '#16212e'
    ctx.fillRect(x, 140, 110, 42)
    ctx.fillStyle = '#9aa4ae'
    ctx.font = '13px system-ui, sans-serif'
    ctx.textAlign = 'left'
    ctx.fillText(k, x + 8, 152)
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 22px system-ui, sans-serif'
    ctx.textAlign = 'right'
    ctx.fillText(v, x + 102, 163)
  })
  return `${readout?.state ?? 'idle'}|${processName}`
}

export function buildBays(mats: FabMaterials, attach: Attachments): Bays {
  const g = new THREE.Group()
  const H = FAB.ceilingHeight
  const colliders: Array<{ minX: number; maxX: number; minZ: number; maxZ: number }> = []
  // A tool's boards: one over its door, and a second over the door in the
  // far wall of a bay it has to itself.
  const andons = new Map<string, Array<{ tile: number; bay: string; key: string }>>()
  let andonTiles = 0

  const bayXs = [...new Set(TOOLS.filter((t) => t.position[2] !== 0).map((t) => t.position[0]))].sort((a, b) => a - b)
  const bayEdge = (x: number, dir: -1 | 1): number => {
    const i = bayXs.indexOf(x)
    const n = bayXs[i + dir]
    return n === undefined ? x + dir * 7 : (x + n) / 2
  }

  const glove = new THREE.BoxGeometry(0.16, 0.24, 0.1)
  const wipes = new THREE.BoxGeometry(0.28, 0.16, 0.12)
  const windows: ChaseWindow[] = []
  const doors: ChaseDoor[] = []

  // Every stretch of wall a bay has: one behind each tool, and - in a bay
  // with a tool on one side only - the wall across the aisle from it. That
  // wall is still the bay's, and a real one is fitted like any other: a
  // door into its chase, a window, the bay's board over the door. Left bare
  // it was a blank white run the length of the bay (QDT-24's, bay 12).
  const walls: Array<{ tool: Tool; side: number; empty: boolean }> = TOOLS.filter((t) => t.position[2] !== 0).map((tool) => ({
    tool,
    side: Math.sign(tool.position[2]),
    empty: false,
  }))
  if (!BALLROOM) {
    for (const bx of bayXs) {
      const here = walls.filter((w) => w.tool.position[0] === bx)
      if (here.length === 0) continue
      for (const side of [-1, 1]) {
        if (!here.some((w) => w.side === side)) walls.push({ tool: here[0].tool, side, empty: true })
      }
    }
  }

  for (const { tool, side, empty } of walls) {
    const size = toolSize(tool)
    const x = tool.position[0]
    // The face's extent along the wall, the side furniture included. The
    // face's local +x runs against world x on the -z side. An empty wall
    // has no face; the nominal span keeps its door and window apart.
    const reach = SIDE_REACH[tool.kind] ?? { minus: 0.3, plus: 0.3 }
    const facing = aisleDirection(tool)
    const faceMin = empty ? x - 1.2 : x - size.w / 2 - (facing > 0 ? reach.plus : reach.minus)
    const faceMax = empty ? x + 1.2 : x + size.w / 2 + (facing > 0 ? reach.minus : reach.plus)
    const left = bayEdge(x, -1)
    const right = bayEdge(x, 1)
    /** Clear of the face and its side furniture (SIDE_REACH carries the margin). */
    const offFace = (fx: number, halfWidth: number): boolean => fx + halfWidth < faceMin || fx - halfWidth > faceMax

    // --- the chase door: on whichever side of the face has the room ---
    //
    // "Room" means room on BOTH sides of the wall. This used to be placed
    // off `faceMax` alone - the face and its side furniture - and a doorway
    // needs the floor behind it as much as the floor in front: `buildTool`
    // stands a pump box 0.95 m and a gas box 0.85 m past the mainframe, in
    // the chase, a stride inside the wall. Sixteen of the twenty doors on
    // this floor opened onto the side of one of them, close enough that
    // there was no line through the doorway that also cleared the box. They
    // could be walked up to, and opened, and not walked through.
    //
    // So the door clears whichever is wider, the face's furniture or the
    // chase hardware, and it keeps clear of the bay divider at the bay edge
    // too - that also stands on the wall line. A bay with no room left for
    // a door that can be used gets no door, which is better than a CHASE
    // ACCESS sign over one that cannot.
    const chaseX = empty ? 0 : size.w / 2 + 0.95
    const outMax = Math.max(faceMax, x + chaseX)
    const outMin = Math.min(faceMin, x - chaseX)
    // Inside the bay, and not where the stocker stands - it runs from the
    // wall back into the chase and closes the doorway from behind exactly
    // the way the pump box did.
    const usable = (dx: number): boolean =>
      dx > left + 0.8 && dx < right - 0.8 && Math.abs(dx - STOCKER.x) > STOCKER.w / 2 + 0.95
    const doorX = BALLROOM ? null : usable(outMax + 0.62) ? outMax + 0.62 : usable(outMin - 0.62) ? outMin - 0.62 : null

    // --- the andon: the bay's own board ---
    //
    // Over the chase door in a bay-and-chase hall, on the wall. In a
    // ballroom there is no wall, and a real one hangs the board from the
    // ceiling over the tool it reports, which is where this goes: a hand
    // behind the row line, above the mainframe, on two drop rods.
    const andonAt = (ax: number, hung: boolean): void => {
      const tile = andonTiles
      if (tile >= ANDON_TILES) return
      andonTiles++
      const key = paintAndon(tile, bayNumber(tool), tool, null, '', tool.id === MAINTENANCE_TOOL)
      if (hung) {
        const az = side * (BULKHEAD_Z + 0.45)
        const ay = 3.05
        const frame = new THREE.Mesh(box(1.3, 0.7, 0.06), mats.conduitDark)
        frame.position.set(ax, ay, az)
        g.add(frame)
        const board = new THREE.Mesh(andonQuad(tile), ANDON_MAT)
        board.rotation.y = side > 0 ? Math.PI : 0
        board.position.set(ax, ay, az - side * 0.035)
        g.add(board)
        for (const dx of [-0.5, 0.5]) {
          const rod = new THREE.Mesh(cyl(0.012, 0.012, H - ay - 0.35, 6), mats.conduit)
          rod.position.set(ax + dx, (ay + 0.35 + H) / 2, az)
          g.add(rod)
        }
      } else {
        const frame = againstWall(g, 1.3, 0.7, 0.06, mats.conduitDark, ax, 2.95, side)
        frame.castShadow = false
        onWall(g, andonQuad(tile), ANDON_MAT, ax, 2.95, side, 0.065)
      }
      andons.set(tool.id, [...(andons.get(tool.id) ?? []), { tile, bay: bayNumber(tool), key }])
    }
    if (BALLROOM) andonAt(x, true)

    if (doorX !== null) {
      // The leaf hangs on a hinge at its +x edge, in a doorway cut through
      // the wall (buildBulkhead), and swings into the chase for whoever
      // walks up to it - see `animate`. Its parts are merged on their own,
      // since the leaf moves.
      const leaf = new THREE.Group()
      const hingeX = doorX + DOOR_W / 2
      leaf.position.set(hingeX, 0, side * BULKHEAD_Z)
      const panel = new THREE.Mesh(box(DOOR_W - 0.04, DOOR_H - 0.04, 0.05), mats.toolPanel)
      panel.position.set(-DOOR_W / 2, DOOR_H / 2, 0)
      panel.castShadow = true
      leaf.add(panel)
      for (const dz of [-1, 1]) {
        const port = new THREE.Mesh(new THREE.PlaneGeometry(0.3, 0.5), mats.screen)
        port.rotation.y = dz > 0 ? 0 : Math.PI
        port.position.set(-DOOR_W / 2, 1.55, dz * 0.03)
        leaf.add(port)
        const handle = new THREE.Mesh(box(0.04, 0.22, 0.05), mats.steel)
        handle.position.set(-DOOR_W + 0.12, 1.05, dz * 0.06)
        leaf.add(handle)
      }
      mergeStatics(leaf, mats)
      g.add(leaf)
      doors.push({ x: doorX, side, toolId: tool.id, leaf, open: 0 })
      onWall(g, new THREE.PlaneGeometry(0.6, 0.15), signMaterial('CHASE ACCESS', '#e8ecf0', '#14181d', 320, 80), doorX, 2.35, side, 0.06)

      // --- the andon over the door ---
      andonAt(doorX, false)
    }

    // --- the viewing window into the chase, beside the face ---
    //
    // On whichever side of the face the door did not take, if the bay has
    // the room. Through it: the tool's own process side - chambers, pump,
    // gas box - and the chase running on behind the next face.
    //
    // No wall, no window: a ballroom shows the same process side in the
    // open, and `dressChase` would stand its backdrop across the gap.
    if (!BALLROOM) {
      const half = WINDOW_W / 2 + 0.15
      const candidates = [faceMax + 0.15 + WINDOW_W / 2, faceMin - 0.15 - WINDOW_W / 2]
      const wx = candidates.find(
        (c) =>
          c - half > left + 0.35 &&
          c + half < right - 0.35 &&
          // A bay that got no door has nothing here to keep clear of.
          (doorX === null || Math.abs(c - doorX) > half + 0.6),
      )
      if (wx !== undefined) {
        const win = { x: wx, side, w: WINDOW_W }
        windows.push(win)
        dressChase(g, mats, win)
      }
    }
    /** A wall fixture shifted clear of the bay's window, if it would sit on it. */
    const clearOfWindow = (fx: number, halfWidth: number): number => {
      for (const win of windows) {
        if (win.side !== side) continue
        const gap = win.w / 2 + halfWidth + 0.15
        if (Math.abs(fx - win.x) < gap) return win.x + gap * (fx >= win.x ? 1 : -1)
      }
      return fx
    }

    // --- dispensers on the pilaster at the bay's +x edge ---
    //
    // (The -x edge of the first bay is where the stockers stand.)
    // Clear of the bay divider, which now stands out into the hall carrying
    // the andon station: two dispensers tucked against its side read as
    // boxes floating behind it rather than as anything mounted on a wall.
    //
    // Wall-mounted, so not in a ballroom - the bay-entrance station on the
    // divider (builders.ts, `buildAisleFurniture`) carries the gloves there.
    //
    // Unless the face's own furniture stands there: EUV-22's scanner runs
    // almost to the divider. Then the bay's other edge, if that is clear -
    // and never on the stocker's.
    const pilasterX = [right - 0.95, right - 0.55, left + 0.95].find(
      (px) => offFace(px, 0.2) && (doorX === null || Math.abs(px - doorX) > 0.65) && Math.abs(px - STOCKER.x) > STOCKER.w / 2 + 0.5,
    )
    if (!BALLROOM && pilasterX !== undefined && Math.abs(pilasterX) < FAB.length / 2 - 8) {
      // ON the wall. These sat at BULKHEAD_Z - 0.45, and the wall's face is
      // at BULKHEAD_Z - 0.1, so both boxes floated 0.30 m clear of it with
      // nothing behind them - which is exactly why they read as blank white
      // slabs stuck in mid-air rather than as anything mounted.
      //
      // And a dispenser is not a box: it is a holder with something IN it.
      // What identifies one across a room is the carton's colour showing
      // through the front and the aperture you pull from, so both get a
      // backplate, an opening, and contents.
      const wallZ = (depth: number): number => side * (BULKHEAD_Z - 0.1 - depth / 2)
      const face = (depth: number): number => side * (BULKHEAD_Z - 0.1 - depth - 0.004)

      const gBack = new THREE.Mesh(box(0.2, 0.28, 0.012), mats.steel)
      gBack.position.set(pilasterX, 1.45, wallZ(0.012))
      g.add(gBack)
      const disp = new THREE.Mesh(glove, DISPENSER)
      disp.position.set(pilasterX, 1.45, wallZ(0.1))
      g.add(disp)
      // The nitrile carton inside, and the cuff of the top glove pulled
      // through the aperture - the one blue thing on a white wall.
      const carton = new THREE.Mesh(box(0.13, 0.16, 0.02), MAT_BLUE)
      carton.position.set(pilasterX, 1.47, face(0.1))
      g.add(carton)
      const cuff = new THREE.Mesh(box(0.05, 0.035, 0.02), MAT_BLUE)
      cuff.position.set(pilasterX, 1.33, face(0.1))
      g.add(cuff)

      const wBack = new THREE.Mesh(box(0.32, 0.2, 0.012), mats.steel)
      wBack.position.set(pilasterX, 1.1, wallZ(0.012))
      g.add(wBack)
      const wipe = new THREE.Mesh(wipes, DISPENSER)
      wipe.position.set(pilasterX, 1.1, wallZ(0.12))
      g.add(wipe)
      const slot = new THREE.Mesh(box(0.15, 0.022, 0.014), CAVITY)
      slot.position.set(pilasterX, 1.05, face(0.12))
      g.add(slot)
      // One wipe left half-pulled, which is what a used dispenser looks like.
      const sheet = new THREE.Mesh(box(0.11, 0.05, 0.006), DISPENSER)
      sheet.position.set(pilasterX, 1.02, face(0.12))
      g.add(sheet)
    }

    // --- an extinguisher every other bay, with its sign ---
    // On the wall, so not in a ballroom. (Phase 2: on a stand by the divider.)
    if (!BALLROOM && bayXs.indexOf(x) % 2 === 0) {
      // Clear of the dispensers on the pilaster at right - 0.95, which moved
      // out to miss the bay divider and landed on top of the extinguisher.
      const ex = clearOfWindow(
        [right - 1.9, left + 1.9].find(
          (c) => offFace(c, 0.2) && (doorX === null || Math.abs(c - doorX) > 0.65) && Math.abs(c - STOCKER.x) > STOCKER.w / 2 + 0.5,
        ) ?? right - 1.9,
        0.2,
      )
      const bottle = new THREE.Mesh(cyl(0.09, 0.09, 0.55, 12), EMO_RED)
      bottle.position.set(ex, 0.95, side * (BULKHEAD_Z - 0.28))
      g.add(bottle)
      const neck = new THREE.Mesh(cyl(0.03, 0.03, 0.12, 8), mats.conduitDark)
      neck.position.set(ex, 1.28, side * (BULKHEAD_Z - 0.28))
      g.add(neck)
      onWall(g, new THREE.PlaneGeometry(0.28, 0.28), signMaterial('FIRE', '#d8262c', '#ffffff', 128, 128), ex, 1.75, side)
    }

    // --- eyewash and safety shower at the chemical bays ---
    if (!empty && (tool.kind === 'wetbench' || tool.kind === 'plating')) {
      // Off the bay's edge in a bay-and-chase hall, where the bay is wide;
      // off the FACE in a ballroom, where the packed row leaves the edge
      // inside the neighbour's side furniture.
      const ex = clearOfWindow(
        BALLROOM ? (side > 0 ? faceMin - 0.75 : faceMax + 0.75) : side > 0 ? left + 1.9 : right - 1.9,
        0.35,
      )
      const z = side * (BULKHEAD_Z - 0.5)
      const post = new THREE.Mesh(cyl(0.03, 0.03, 2.4, 10), SAFETY_GREEN)
      post.position.set(ex, 1.2, z)
      g.add(post)
      const bowl = new THREE.Mesh(cyl(0.22, 0.14, 0.12, 16), SAFETY_GREEN)
      bowl.position.set(ex, 1.0, z + side * -0.22)
      g.add(bowl)
      const shower = new THREE.Mesh(cyl(0.2, 0.2, 0.04, 16), SAFETY_GREEN)
      shower.position.set(ex, 2.35, z + side * -0.25)
      g.add(shower)
      const shArm = new THREE.Mesh(cyl(0.02, 0.02, 0.3, 8), SAFETY_GREEN)
      shArm.rotation.x = Math.PI / 2
      shArm.position.set(ex, 2.38, z + side * -0.12)
      g.add(shArm)
      const pull = new THREE.Mesh(new THREE.TorusGeometry(0.06, 0.012, 6, 16), mats.steel)
      pull.position.set(ex + 0.12, 1.9, z + side * -0.15)
      g.add(pull)
      // The station stands on its own post either way; only its sign needs
      // a wall behind it.
      if (!BALLROOM) onWall(g, new THREE.PlaneGeometry(0.34, 0.34), signMaterial('EYEWASH', '#1f9d55', '#ffffff', 128, 128), ex, 2.0, side)
      colliders.push({ minX: ex - 0.35, maxX: ex + 0.35, minZ: Math.min(z, z - side * 0.5), maxZ: Math.max(z, z - side * 0.5) })
    }
  }

  // --- side-track buffers: carriers on shelves under the hoist rail ---
  //
  // Between the faces, where the rail passes nothing: brackets from the
  // ceiling, a shelf, a row of carriers, at the height a hoist can take
  // one without lowering far. Their carriers file into the same instanced
  // set as the load ports'.
  const stbY = 2.45
  const stbZ = BULKHEAD_Z - 0.35
  const bracket = new THREE.CylinderGeometry(0.02, 0.02, H - stbY, 6)
  const brackets: THREE.Matrix4[] = []
  const shelves: THREE.Matrix4[] = []
  const stbSpots: Array<[number, number]> = []
  // In the gap between two bays' faces: clear of both faces' side
  // furniture, or not at all.
  const clearOf = (x: number, side: number): boolean =>
    TOOLS.every((t) => {
      if (Math.sign(t.position[2]) !== side) return true
      const size = toolSize(t)
      const reach = SIDE_REACH[t.kind] ?? { minus: 0.3, plus: 0.3 }
      const facing = aisleDirection(t)
      const lo = t.position[0] - size.w / 2 - (facing > 0 ? reach.plus : reach.minus) - 1.1
      const hi = t.position[0] + size.w / 2 + (facing > 0 ? reach.minus : reach.plus) + 1.1
      return x < lo || x > hi
    })
  // Offset from the gap's centre, never on it: the bay divider stands there
  // now (PARTITION_NEAR_Z), and a shelf hung on the centreline was a row of
  // carriers with a wall through the middle of it. A gap with no clear spot
  // on either side simply gets no buffer, which is also true of a real bay.
  for (let i = 0; i + 1 < bayXs.length; i++) {
    const mid = (bayXs[i] + bayXs[i + 1]) / 2
    for (const side of [-1, 1]) {
      for (const dx of [1.5, -1.5]) {
        if (clearOf(mid + dx, side)) {
          stbSpots.push([mid + dx, side])
          break
        }
      }
    }
  }
  for (const [x, side] of stbSpots) {
    const n = 4
    const w = n * 0.42
    shelves.push(new THREE.Matrix4().makeTranslation(x, stbY, side * stbZ).multiply(new THREE.Matrix4().makeScale(w, 1, 1)))
    for (const dx of [-w / 2 + 0.05, w / 2 - 0.05]) {
      brackets.push(new THREE.Matrix4().makeTranslation(x + dx, stbY + (H - stbY) / 2, side * stbZ))
    }
    for (let k = 0; k < n; k++) {
      // One slot in three empty, the way a working buffer is.
      if ((k + Math.round(x)) % 3 === 0) continue
      attach.foups.push(
        new THREE.Matrix4().makeRotationY(side > 0 ? Math.PI : 0).setPosition(x - w / 2 + 0.21 + k * 0.42, stbY + 0.19, side * stbZ),
      )
    }
  }
  const shelfMesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 0.04, 0.44), mats.toolPanel, shelves.length)
  shelves.forEach((m, i) => shelfMesh.setMatrixAt(i, m))
  shelfMesh.frustumCulled = false
  g.add(shelfMesh)
  const bracketMesh = new THREE.InstancedMesh(bracket, mats.conduit, brackets.length)
  brackets.forEach((m, i) => bracketMesh.setMatrixAt(i, m))
  bracketMesh.frustumCulled = false
  g.add(bracketMesh)

  // --- the reticle stocker beside the scanner ---
  const litho = TOOLS.find((t) => t.kind === 'litho')
  if (litho) {
    const side = Math.sign(litho.position[2])
    const size = toolSize(litho)
    // Past the scanner, which stands on the face's -x side (see SILHOUETTES).
    const x = litho.position[0] + (aisleDirection(litho) > 0 ? 1 : -1) * (size.w / 2 + 3.15 + 0.55)
    const d = 0.5
    const cab = againstWall(g, 1.1, 2.0, d, mats.toolBody, x, 1.0, side)
    cab.castShadow = true
    const zFront = side * (BULKHEAD_Z - 0.1 - d)
    const pane = new THREE.Mesh(box(0.9, 1.5, 0.01), mats.glass)
    pane.position.set(x, 1.15, zFront - side * 0.006)
    g.add(pane)
    const pods = new THREE.InstancedMesh(new THREE.BoxGeometry(0.2, 0.05, 0.2), RETICLE_POD, 10)
    let i = 0
    for (let row = 0; row < 5; row++) {
      for (const dx of [-0.22, 0.22]) {
        pods.setMatrixAt(i++, new THREE.Matrix4().makeTranslation(x + dx, 0.55 + row * 0.3, zFront + side * 0.15))
      }
    }
    g.add(pods)
    onWall(g, new THREE.PlaneGeometry(0.7, 0.16), signMaterial('RETICLE STOCKER', '#e8ecf0', '#14181d', 256, 64), x, 2.2, side, d + 0.01)
    colliders.push({ minX: x - 0.65, maxX: x + 0.65, minZ: Math.min(zFront, side * BULKHEAD_Z), maxZ: Math.max(zFront, side * BULKHEAD_Z) })
  }

  // --- extinguishers in a ballroom: on the bay dividers ---
  //
  // No wall to hang them on, so they go where a ballroom's go - on the
  // column at the bay entrance, on the side the bin is not, behind the
  // station's own fittings, one at every other bay, the sign above it
  // facing along the aisle the way the station does.
  if (BALLROOM) {
    const xs = [...new Set(partitions().map((p) => p.x))].sort((a, b) => a - b)
    partitions().forEach((p) => {
      if (xs.indexOf(p.x) % 2 !== 0) return
      const ex = p.x - 0.13 - 0.1
      // On the wall-line side of the divider, in whichever aisle it stands.
      const ez = partitionZ(p, BULKHEAD_Z - 0.2)
      const bottle = new THREE.Mesh(cyl(0.09, 0.09, 0.55, 12), EMO_RED)
      bottle.position.set(ex, 0.95, ez)
      g.add(bottle)
      const neck = new THREE.Mesh(cyl(0.03, 0.03, 0.12, 8), mats.conduitDark)
      neck.position.set(ex, 1.28, ez)
      g.add(neck)
      const sign = new THREE.Mesh(new THREE.PlaneGeometry(0.28, 0.28), signMaterial('FIRE', '#d8262c', '#ffffff', 128, 128))
      sign.rotation.y = -Math.PI / 2
      sign.position.set(p.x - 0.13 - 0.006, 1.75, ez)
      g.add(sign)
    })
  }

  // --- exit signs at the hall's ends, the evacuation map by the airlock ---
  for (const end of [-1, 1]) {
    const x = end * (FAB.length / 2 - 0.15)
    const sign = new THREE.Mesh(tint(new THREE.PlaneGeometry(0.6, 0.22), EXIT_GREEN), UNLIT)
    sign.rotation.y = end > 0 ? -Math.PI / 2 : Math.PI / 2
    sign.position.set(x, H - 0.6, 0)
    g.add(sign)
    const text = new THREE.Mesh(new THREE.PlaneGeometry(0.56, 0.18), signMaterial('EXIT', '#1f9d55', '#ffffff', 256, 80))
    text.rotation.y = end > 0 ? -Math.PI / 2 : Math.PI / 2
    text.position.set(x - end * 0.01, H - 0.6, 0)
    g.add(text)
  }
  // Past the stocker, toward the airlock: at the first bay's edge it hung
  // behind the stocker's shelves.
  if (!BALLROOM) onWall(g, new THREE.PlaneGeometry(0.6, 0.45), signMaterial('EVACUATION PLAN', '#f4f6f8', '#14181d', 256, 192), STOCKER.x - STOCKER.w / 2 - 1.2, 1.6, -1)

  // --- the sticky mat inside the airlock door ---
  const mat = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 0.9), MAT_BLUE)
  mat.rotation.x = -Math.PI / 2
  mat.position.set(GOWNING.position[0], 0.004, GOWNING.position[2] + 2.6)
  g.add(mat)

  // --- the tool down for maintenance ---
  const pm = TOOLS.find((t) => t.id === MAINTENANCE_TOOL)
  if (pm) {
    const facing = aisleDirection(pm)
    const faceZ = toolFaceZ(pm)
    const { x: px, w, h, y } = MAINTENANCE_PANEL
    // The face's local x runs against world x on the +z side.
    const wx = pm.position[0] + (facing > 0 ? -px : px)
    /** World x per metre of the face's local +x: toward the front end. */
    const inboard = facing > 0 ? -1 : 1
    // The cavity where the skin was: dark, with the frame members and a
    // couple of modules inside, proud of the face so it does not fight it.
    //
    // 38 mm proud, and that number is load-bearing. Everything a tool wears
    // on its face stands off it a little - printed labels 14 mm, placards and
    // indicator strips 30, a flush access door 30 - and every one of those is
    // stuck to skin that is now off the tool and leaning against the wall. At
    // 12 mm they floated in the hole: a nitrogen placard hanging in mid-air
    // where the panel it was printed on used to be. The frame members behind
    // start at 40 mm, so they still show.
    const cavity = new THREE.Mesh(box(w, h, 0.02), CAVITY)
    cavity.position.set(wx, y, faceZ + facing * 0.038)
    g.add(cavity)
    if (pm.kind === 'furnace') {
      // What is behind a vertical furnace's skin: the boat elevator, and
      // on it the quartz boat with its stack of wafers, lowered out of
      // the tube for the PM - the one sight that tells a student what
      // the cabinet is for. The first version showed rack modules, which
      // is the inside of a tester, not a furnace.
      //
      // Sized off the OPENING rather than written out, so the boat cannot
      // stand taller than the hole it is seen through - which is what
      // happened the moment the panel moved down the face.
      const sill = y - h / 2
      const boatH = Math.min(h - 0.45, 1.15)
      const boatY = sill + 0.26 + boatH / 2
      const zb = faceZ + facing * 0.2
      const column = new THREE.Mesh(box(0.12, h - 0.1, 0.1), mats.steel)
      column.position.set(wx - w / 2 + 0.12, y, faceZ + facing * 0.08)
      g.add(column)
      const pedestal = new THREE.Mesh(cyl(0.2, 0.2, 0.06, 20), mats.steel)
      pedestal.position.set(wx + 0.08, sill + 0.2, zb)
      g.add(pedestal)
      const wafers = new THREE.Mesh(cyl(0.15, 0.15, boatH - 0.1, 24), mats.conduitDark)
      wafers.position.set(wx + 0.08, boatY, zb)
      g.add(wafers)
      for (const [dx, dz] of [[-0.16, 0], [0.08, -0.14], [0.08, 0.14]] as const) {
        const rod = new THREE.Mesh(cyl(0.012, 0.012, boatH, 6), mats.glass)
        rod.position.set(wx + 0.08 + dx, boatY, zb + dz)
        g.add(rod)
      }
      const boat = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.19, boatH, 24, 1, true), mats.glass)
      boat.position.set(wx + 0.08, boatY, zb)
      g.add(boat)
    } else {
      for (const dy of [-0.5, 0.15, 0.6]) {
        const module = new THREE.Mesh(box(w - 0.2, 0.18, 0.06), mats.conduitDark)
        module.position.set(wx, y + dy, faceZ + facing * 0.05)
        g.add(module)
      }
    }
    for (const dx of [-w / 2 + 0.04, w / 2 - 0.04]) {
      const member = new THREE.Mesh(box(0.05, h, 0.05), mats.steel)
      member.position.set(wx + dx, y, faceZ + facing * 0.04)
      g.add(member)
    }
    // The skin itself, hinged open on the far side and standing out into
    // the bay, the lockout tag on its edge.
    const skin = new THREE.Group()
    skin.position.set(wx + (facing > 0 ? w / 2 : -w / 2), y, faceZ)
    skin.rotation.y = facing * -1.75
    const panel = new THREE.Mesh(box(w, h, 0.03), mats.toolBody)
    panel.position.set(facing > 0 ? -w / 2 : w / 2, 0, 0)
    panel.castShadow = true
    skin.add(panel)
    const tag = new THREE.Mesh(tint(new THREE.PlaneGeometry(0.09, 0.15), TAG_RED_TINT), UNLIT)
    tag.position.set(facing > 0 ? -w + 0.1 : w - 0.1, -0.05, -0.02)
    skin.add(tag)
    const tagText = new THREE.Mesh(new THREE.PlaneGeometry(0.085, 0.06), signMaterial('DANGER', '#d8262c', '#ffffff', 128, 64))
    tagText.position.set(facing > 0 ? -w + 0.1 : w - 0.1, 0.02, -0.021)
    skin.add(tagText)
    const lock = new THREE.Mesh(new THREE.TorusGeometry(0.02, 0.006, 6, 12), mats.steel)
    lock.position.set(facing > 0 ? -w + 0.1 : w - 0.1, 0.06, -0.02)
    skin.add(lock)
    mergeStatics(skin, mats, [UNLIT, ...signCache.values()])
    g.add(skin)
    // The step stool and the tool bag, set down BESIDE the panel rather
    // than under the technician. He was standing on top of the stool - the
    // open panel reaches from 0.2 m to 1.9 m and a man on the floor can
    // work all of it, so a man up on a box read as a man standing on a
    // crate. Steel, not the levelling pads' yellow: a fab step stool is a
    // stainless two-tread thing, and it was the wrong colour as well as in
    // the wrong place.
    //
    // INBOARD of the panel, since the panel is now at the skin's outboard
    // end: laid out the other way they stood past the corner of the tool.
    const stoolZ = faceZ + facing * 0.6
    const sx = wx + inboard * 0.78
    const stool = new THREE.Mesh(box(0.42, 0.28, 0.36), mats.steel)
    stool.position.set(sx, 0.14, stoolZ)
    g.add(stool)
    const tread = new THREE.Mesh(box(0.44, 0.03, 0.38), mats.conduitDark)
    tread.position.set(sx, 0.29, stoolZ)
    g.add(tread)
    const bag = new THREE.Mesh(box(0.42, 0.24, 0.26), mats.conduitDark)
    bag.position.set(wx + inboard * 1.4, 0.12, stoolZ)
    g.add(bag)
    // The cones a PM puts out, the tape across them, and the 'DOWN' notice
    // hung on it.
    //
    // ON THE BARRIER, NOT ON THE FACE. It used to be a placard on the skin
    // beside the panel and there is no skin beside the panel now: outboard is
    // the tool's own corner, above are the hazard placards, and a hand
    // inboard the HMI screen stands 0.62 m proud and hides whatever is behind
    // it from most of the aisle - which is where it went, and it read as a
    // red sliver. A barrier is where a shift hangs this anyway. The notice is
    // addressed to whoever is walking up, not to whoever is already at the
    // tool, and the tool's own screen is telling that man it is down in
    // hundred-millimetre letters.
    const barrierZ = faceZ + facing * 0.95
    for (const dx of [-0.9, 0.9]) {
      const cone = new THREE.Mesh(cyl(0.02, 0.15, 0.5, 12), mats.levellingPad)
      cone.position.set(wx + dx, 0.25, barrierZ)
      g.add(cone)
    }
    const tape = new THREE.Mesh(box(1.8, 0.035, 0.015), mats.levellingPad)
    tape.position.set(wx, 0.5, barrierZ)
    g.add(tape)
    const notice = new THREE.Mesh(new THREE.PlaneGeometry(0.5, 0.22), signMaterial('DOWN · PM', '#d8262c', '#ffffff', 256, 112))
    notice.rotation.y = facing > 0 ? 0 : Math.PI
    notice.position.set(wx, 0.36, barrierZ + facing * 0.012)
    g.add(notice)
    // The PM footprint, which has to fit the aisle it is set up in: 1.2 m of
    // cones and stool proud of the face was a third of a 5.4 m walkway, and
    // it swallowed the standing spot the assistant walks a student to.
    // Along the wall it has to reach the tool bag, which is the outermost
    // thing set down and was outside it - you could walk through the bag. Out
    // into the aisle it has to reach the barrier, for the same reason and a
    // better one: a cordon you can walk through is not a cordon, and this one
    // is the first thing on the floor that teaches a student to stop at one.
    const edges = [wx - inboard * 1.0, wx + inboard * 1.65]
    const back = faceZ + facing * 1.02
    colliders.push({
      minX: Math.min(...edges),
      maxX: Math.max(...edges),
      minZ: Math.min(faceZ, back),
      maxZ: Math.max(faceZ, back),
    })
  }

  // --- a manual guided cart, parked at CMP with the technician beside it ---
  const cmp = TOOLS.find((t) => t.id === 'cmp')
  if (cmp) {
    const facing = aisleDirection(cmp)
    const size = toolSize(cmp)
    const x = cmp.position[0] + (facing > 0 ? -(size.w / 2 - 0.4) : size.w / 2 - 0.4) - facing * 0.9
    const z = toolFaceZ(cmp) + facing * 1.5
    const cart = new THREE.Group()
    cart.position.set(x, 0, z)
    const deck = new THREE.Mesh(box(0.7, 0.05, 0.5), mats.toolPanel)
    deck.position.y = 0.7
    cart.add(deck)
    const lower = new THREE.Mesh(box(0.7, 0.05, 0.5), mats.toolPanel)
    lower.position.y = 0.25
    cart.add(lower)
    for (const dx of [-0.32, 0.32]) {
      for (const dz of [-0.22, 0.22]) {
        const leg = new THREE.Mesh(box(0.03, 0.72, 0.03), mats.steel)
        leg.position.set(dx, 0.4, dz)
        cart.add(leg)
        const caster = new THREE.Mesh(cyl(0.04, 0.04, 0.03, 10), mats.conduitDark)
        caster.rotation.z = Math.PI / 2
        caster.position.set(dx, 0.04, dz)
        cart.add(caster)
      }
    }
    const handle = new THREE.Mesh(cyl(0.015, 0.015, 0.5, 8), mats.steel)
    handle.rotation.x = Math.PI / 2
    handle.position.set(-0.36, 0.95, 0)
    cart.add(handle)
    for (const dx of [-0.36, -0.36]) {
      const upright = new THREE.Mesh(cyl(0.015, 0.015, 0.25, 8), mats.steel)
      upright.position.set(dx, 0.83, 0)
      cart.add(upright)
    }
    mergeStatics(cart, mats)
    attach.foups.push(new THREE.Matrix4().makeTranslation(x + 0.05, 0.9, z))
    g.add(cart)
    colliders.push({ minX: x - 0.45, maxX: x + 0.45, minZ: z - 0.35, maxZ: z + 0.35 })
  }

  // A few hundred small meshes into one per material - the hall's far view
  // is a draw-call budget, and a wall of signs and handles is exactly the
  // kind of thing that spends it. The andon screens keep their own
  // textures and the buffers are instanced already.
  mergeStatics(g, mats, [SAFETY_GREEN, UNLIT, MAT_BLUE, DISPENSER, CAVITY, ANDON_MAT, ...DROP_MATS,...signCache.values()])
  // After the merge, so the two walls stay two meshes: front-to-back
  // ordering between separate objects is what keeps the far view cheap.
  // No bulkhead in a ballroom: the rows stand in the open, and the walk
  // between them and the service side is gated by nothing but the tools.
  if (!BALLROOM) for (const mesh of buildBulkhead(mats, windows, doors)) g.add(mesh)

  // The bulkhead's colliders, with a gap at every doorway, so the chase is
  // a place a student can walk into; and the chase's own furniture.
  const far = FAB.bayDepth + FAB.aisleHalfWidth
  for (const side of [-1, 1]) {
    if (!BALLROOM) {
      const gaps = doors.filter((d) => d.side === side).map((d) => d.x).sort((a, b) => a - b)
      let x = -FAB.length / 2
      const zs = [side * (BULKHEAD_Z - 0.15), side * (BULKHEAD_Z + 0.15)]
      for (const gx of [...gaps, FAB.length / 2 + DOOR_W]) {
        const x1 = gx - DOOR_W / 2 - 0.15
        if (x1 > x) colliders.push({ minX: x, maxX: x1, minZ: Math.min(...zs), maxZ: Math.max(...zs) })
        x = gx + DOOR_W / 2 + 0.15
      }
    }
    // The hall's outer wall, which both layouts have.
    colliders.push({ minX: -FAB.length / 2, maxX: FAB.length / 2, minZ: Math.min(side * (far - 0.5), side * far), maxZ: Math.max(side * (far - 0.5), side * far) })
  }
  for (const win of windows) {
    const z0 = win.side * (BULKHEAD_Z + 0.4)
    const z1 = win.side * (BULKHEAD_Z + 1.7)
    colliders.push({ minX: win.x - 0.9, maxX: win.x + 0.9, minZ: Math.min(z0, z1), maxZ: Math.max(z0, z1) })
  }

  const animate = (dt: number, eye: THREE.Vector3): void => {
    for (const d of doors) {
      // Open for anyone within reach of the doorway, either side of it.
      const dx = eye.x - d.x
      const dz = eye.z - d.side * BULKHEAD_Z
      const near = eye.y > -0.5 && dx * dx + dz * dz < 1.8 * 1.8
      const target = near ? 1 : 0
      if (d.open === target) continue
      d.open += Math.sign(target - d.open) * Math.min(Math.abs(target - d.open), dt / 0.7)
      // Eased, swinging into the chase, the way a corridor door is hung.
      const s = d.open * d.open * (3 - 2 * d.open)
      d.leaf.rotation.y = d.side * s * 1.65
    }
  }

  const update = (tool: Tool, readout: HmiReadout, processName: string): void => {
    // Only when the board would change: a repaint re-uploads the atlas.
    const key = `${readout.state}|${processName}`
    for (const a of andons.get(tool.id) ?? []) {
      if (key === a.key) continue
      a.key = paintAndon(a.tile, a.bay, tool, readout, processName, tool.id === MAINTENANCE_TOOL)
      andonTexture.needsUpdate = true
    }
  }

  const openings = () => ({
    doors: doors.map((d) => ({ x: d.x, side: d.side, toolId: d.toolId })),
    windows: windows.map((w) => ({ x: w.x, side: w.side, w: w.w })),
  })
  return { group: g, update, animate, colliders: () => colliders, openings }
}
